import isEmpty from 'lodash/isEmpty';
import React from 'react';
import T from 'prop-types';
import { _ } from '@splunk/ui-utils/i18n';
import Heading from '@splunk/react-ui/Heading';
import Markdown from '@splunk/react-ui/Markdown';
import styled from 'styled-components';

const Option = styled.div`
    margin-top: 20px;
`;
const OptionName = styled.div`
    color: #5ca300;
    padding-bottom: 5px;
`;
const PairContainer = styled.div`
    margin-left: 40px;
    margin-top: 10px;
`;
const PairTitle = styled.span`
    display: inline-block;
    color: #8293a7;
    width: 80px;
`;

const Pair = ({ title, text }) => (
    <PairContainer>
        <PairTitle>{`${title}:`}</PairTitle>
        <span>{text}</span>
    </PairContainer>
);

Pair.propTypes = {
    title: T.string,
    text: T.string,
};

const OptionItem = ({ name, type, desc, defaultValue }) => (
    <Option>
        <OptionName>{name}</OptionName>
        <Markdown style={{ marginLeft: 40 }} text={desc} />
        <Pair title={_('Type')} text={type} />
        {defaultValue && <Pair title={_('Default')} text={defaultValue} />}
    </Option>
);

OptionItem.propTypes = {
    name: T.string,
    type: T.string,
    desc: T.string,
    defaultValue: T.string,
};

/**
 * get the type of this option item
 * @method getType
 * @param {ojbect} item option item
 * @returns {string} type
 */
const getType = item => {
    if (item.enum && Array.isArray(item.enum)) {
        const values =
            !isEmpty(item.enum) && typeof item.enum[0] === 'string'
                ? item.enum.map(v => `"${v}"`).join(', ')
                : item.enum.join(', ');

        return `oneOf([${values}])`;
    }
    if (Array.isArray(item.type)) {
        if (isEmpty(item.default)) {
            return item.type.join(' | ');
        }
        let res = item.type.filter(itemType => itemType !== 'string').join(' | ');
        res += ' | ';
        res += `"${item.default}"`;
        return res;
    }
    if (item.type === 'array') {
        return `arrayOf(${item.items.type})`;
    }
    return item.type;
};

/**
 * get the default value as string
 * @method getDefaultValue
 * @param {object | boolean | number | string} defaultValue
 * @returns {string} default value
 */
const getDefaultValue = defaultValue => {
    if (typeof defaultValue === 'boolean' || typeof defaultValue === 'number') {
        return defaultValue.toString();
    }
    if (Array.isArray(defaultValue)) {
        const values = defaultValue.map(v => `"${v}"`).join(', ');
        return `[${values}]`;
    }
    return defaultValue;
};

const OptionsDoc = ({ options, description }) => (
    <div>
        {options.length === 0 ? (
            <Heading level={4}> No Options</Heading>
        ) : (
            <Heading level={4}>Options</Heading>
        )}
        {description && <div>{description}</div>}
        {Object.entries(options).map(([key, item]) => (
            <OptionItem
                key={key}
                name={key}
                type={getType(item)}
                desc={item.description}
                defaultValue={getDefaultValue(item.default)}
            />
        ))}
    </div>
);

OptionsDoc.propTypes = {
    options: T.object.isRequired,
    description: T.string,
};

export default OptionsDoc;
